import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../utils/api';
import { useTranslation } from 'react-i18next';

const LoginPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const redirect = location.state?.redirect || "/dashboard";
  const { login } = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!isLogin && !acceptedPrivacy) {
      setError(t('login.error_privacy'));
      return;
    }

    setLoading(true);

    try {
      const endpoint = isLogin ? "/api/auth/login" : "/api/auth/register";
      const body = isLogin ? { email, password } : { email, password, name };

      const response = await apiFetch(`${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || t('login.error_auth'));
      }

      login(data.user, data.token);

      // Save credentials into the native / browser password manager
      // so the user can autofill next time even if the JWT expires.
      // Feature-detect: not all browsers / webviews implement PasswordCredential.
      try {
        if (window.PasswordCredential && navigator.credentials?.store) {
          const cred = new window.PasswordCredential({
            id: email,
            password,
            name: data.user?.name || email,
          });
          navigator.credentials.store(cred).catch(() => {});
        }
      } catch (_) {
        // Silently ignore – autofill is a nice-to-have, must not break login.
      }

      if (data.user.role === "coach") {
        navigate("/coach-dashboard", { replace: true });
      } else {
        navigate(redirect, { replace: true });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="auth-page" data-testid="login-page">
      <img src="/dragon-logo.png" alt="DragonFit" className="auth-logo" />
      <h1 className="auth-title neon-text">DragonFit</h1>
      <p className="auth-subtitle">{t('login.subtitle')}</p>

      <div className="auth-form">
        <form onSubmit={handleSubmit}>
          {!isLogin && (
            <div className="input-group">
              <label className="input-label">{t('login.name_label')}</label>
              <input
                type="text"
                className="input"
                placeholder={t('login.name_placeholder')}
                value={name}
                onChange={(e) => setName(e.target.value)}
                required={!isLogin}
                autoComplete="name"
                data-testid="register-name-input"
              />
            </div>
          )}

          <div className="input-group">
            <label className="input-label">{t('login.email_label')}</label>
            <input
              type="email"
              className="input"
              placeholder="tu@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="username"
              inputMode="email"
              data-testid="email-input"
            />
          </div>

          <div className="input-group">
            <label className="input-label">{t('login.password_label')}</label>
            <input
              type="password"
              className="input"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete={isLogin ? "current-password" : "new-password"}
              data-testid="password-input"
            />
          </div>

          {!isLogin && (
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "8px",
                marginBottom: "16px",
                fontSize: "13px",
              }}
            >
              <input
                type="checkbox"
                checked={acceptedPrivacy}
                onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                style={{ marginTop: "3px" }}
                data-testid="privacy-checkbox"
              />
              <span style={{ color: "var(--muted-foreground)" }}>
                {t('login.privacy_text')}{" "}
                <Link
                  to="https://dragon-fit-training.vercel.app/privacy"
                  target="_blank"
                  style={{ fontWeight: 600 }}
                >
                  {t('login.privacy_link')}
                </Link>
              </span>
            </div>
          )}

          {error && (
            <p style={{ color: "var(--destructive)", fontSize: "14px", marginBottom: "16px" }}>
              {error}
            </p>
          )}

          <button
            type="submit"
            className="btn btn-primary w-full"
            disabled={loading}
            data-testid="submit-button"
          >
            {loading ? t('login.btn_loading') : isLogin ? t('login.btn_login') : t('login.btn_register')}
          </button>
        </form>

        <p className="auth-switch">
          <Link to="/forgot-password">{t('login.forgot_password')}</Link>
        </p>

        <div className="auth-divider">{t('login.divider')}</div>

        <p className="auth-switch">
          {isLogin ? `${t('login.no_account')} ` : `${t('login.have_account')} `}
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setIsLogin(!isLogin);
            }}
          >
            {isLogin ? t('login.register_link') : t('login.login_link')}
          </a>
        </p>

        <p className="auth-switch">
          {t('login.coach_question')}{" "}
          <span
            style={{ color: "var(--primary)", cursor: "pointer", fontWeight: "600" }}
            onClick={() => navigate("/coach-request")}
          >
            {t('login.coach_access')}
          </span>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
